import React, { useRef, useState } from 'react';
import './NewAccordanceForm.scss';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField } from '@material-ui/core';
import RequiredErrorMessage from '../../../../Util/Errors/RequiredErrorMessage/RequiredErrorMessage';
import CustomDatePicker from '../../../../Util/CustomDatePicker/CustomDatePicker';
import SearchUsers from '../../../../Util/SearchUsers/SearchUsers';
import { AccordanceType } from '../../../../../models/AccordanceType';
import { AccordanceState } from '../../../../../models/AccordanceState';

import { useStyles } from './NewAccordanceForm.Styles';
import { OptionWithCheckBox } from './OptionWithCheckBox/OptionWithCheckBox';
import { TagOptionSelected } from './Tags/TagOptionSelected';
import ImproveWithAIButton from '../../../../Util/Buttons/ImproveWithAIButton/ImproveWithAIButton';
import { getIASuggestion, IASuggestionResponse, IASuggestionPayload, logDecisionIASuggestion } from '../../../../../api/accordanceApi';
import { IA_ERROR_REQUEST_TYPE } from './IAImproveText/IAErrorRequestType';
import { ActionTypeAI, FieldTypeAi } from '../../../../../enums/IA-suggestionEnum';
import { useSelector } from 'react-redux';
import { ACCEPTED_IA_SUGGESTION, REJECTED_IA_SUGGESTION } from '../../../../Projects/ProjectsReport/utils/Constants/AlertStatusConstants';

type Props = {
  isMulti?: boolean;
  criticalIssue?: any;
  formErrors?: any;
  possibleActionsList?: any;
  possibleAction?: any;
  handlePossibleActionChange?: any;
  expirationDate?: any;
  handleExpirationDate: Function;
  responsible?: any;
  handleIemSelected: Function;
  filesList?: any;
  addFileToList?: Function;
  removeFileFromList?: Function;
  editAccordanceData?: any;
  accordanceType: AccordanceType;
  handleDescription?: any;
  description?: string;
  revisionDate?: any;
  accordanceState?: number;
  supervisionActionList?: any;
  handleSupervisionActionChange?: any;
  supervisionAction?: any;
  removeAccordance?: Function;
  addNewAccordance?: Function;
  internalId?: string;
  rootCauseOfCritical?: any;
  rootCause?: any;
  handleRootCauseOfCritical?: any;
  handleRootCause?: any;
  rootCauseCategoriesForm?: any;
  rootCauseForm?: any;
  validateTextareaDescription?: any;
  accordanceDate?: any;
  criticalIssueListShared?: any;
  criticalIssueShared?: any;
  handleCriticalIssueChangeShared?: any;
  linkedProductShared?: any;
  handleLinkedProductShared?: any;
  productFormShared?: any;
  criticalIssueDescriptionShared?: any;
  handleCriticalIssueDescriptionShared?: any;
  validateTextareaCriticalShared?: any;
  issuggestedAgreement?: boolean;
  setAlertConfig?: Function;
};

const displayLabel = (option: any) => {
  return option.label || '';
};

const displayName = (option: any) => {
  return option.name || option.nameProduct;
};

const SingleInputs = (props: Props) => {
  const { t } = useTranslation();

  const {
    isMulti,
    criticalIssue,
    formErrors,
    expirationDate,
    handleExpirationDate,
    responsible,
    handleIemSelected,
    editAccordanceData,
    handleDescription,
    description,
    revisionDate,
    accordanceState,
    removeAccordance,
    addNewAccordance,
    rootCauseOfCritical,
    rootCause,
    handleRootCauseOfCritical,
    handleRootCause,
    rootCauseCategoriesForm,
    rootCauseForm,
    validateTextareaDescription,
    criticalIssueListShared,
    handleCriticalIssueChangeShared,
    linkedProductShared,
    handleLinkedProductShared,
    productFormShared,
    criticalIssueDescriptionShared,
    handleCriticalIssueDescriptionShared,
    validateTextareaCriticalShared,
    issuggestedAgreement = false,
    setAlertConfig
  } = props;

  const MAX_LENGHT_DESCRIPTION = 500;

  const currentProjectId = useSelector<any, number>(state => state.currentProject?.id || 0);

  const isNotRequired = !!editAccordanceData;
  const classes = useStyles({ isNotRequired });
  const criticalIssueDescriptionRef = useRef<HTMLTextAreaElement>(null);
  const [showButtonIA, setShowButtonIA] = useState(false);
  const [isLoadingIA, setIsLoadingIA] = useState(false);
  const [IAResponse, setIAResponse] = useState<IASuggestionResponse | null>(null);
  const [IAErrorRequest, setIAErrorRequest] = useState<string | null>(null);
  const [isAppliedIASugestion, setIsAppliedIASugestion] = useState(false);

  const hasMinimumInputLength = (value?: string) => (value?.trim().length || 0) >= 60;

  const handleCriticalIssueDescriptionChange = (value: string) => {
    setShowButtonIA(hasMinimumInputLength(value));
    validateTextareaCriticalShared(value);
    handleCriticalIssueDescriptionShared(value);

    if (IAResponse) setIAResponse(null);
    if (IAErrorRequest) setIAErrorRequest(null);
    if (isAppliedIASugestion) setIsAppliedIASugestion(false);

    if (criticalIssueDescriptionRef.current) {
      criticalIssueDescriptionRef.current.style.height = 'auto';
      criticalIssueDescriptionRef.current.style.height = criticalIssueDescriptionRef.current.scrollHeight + 'px';
    }
  }

  const handleImproveWithAI = async (originalText: string, field: string) => {
    setIsLoadingIA(true);

    const payload: IASuggestionPayload = {
      QuestionOriginalText: originalText,
      actionType: ActionTypeAI.Generated,
      fieldType: FieldTypeAi.AccordancesCriticalIssues,
      entityType: 'Accordances',
      PreviousSuggestion: '',
    }

    await getIASuggestion(currentProjectId, payload).then(response => {
      setIsLoadingIA(false);
      setIAResponse(response);
    })
    .catch((error) => {
      setIsLoadingIA(false);
      setIAErrorRequest(t(IA_ERROR_REQUEST_TYPE[error?.response?.status] || 'ia_generic_error'));
    });
  }

  const handleUseIAResponse = () => {
    if (!IAResponse?.answer) {
      return;
    }

    handleCriticalIssueDescriptionChange(IAResponse.answer);
    logDecisionIASuggestion(IAResponse, ActionTypeAI.Accepted);
    setAlertConfig && setAlertConfig(ACCEPTED_IA_SUGGESTION);
    setIAResponse(null);
    setIsAppliedIASugestion(true);
  };

  const handleDiscardIAResponse = () => {
    logDecisionIASuggestion(IAResponse!, ActionTypeAI.Discarded);
    setAlertConfig && setAlertConfig(REJECTED_IA_SUGGESTION);
    setIAResponse(null);
  };

  return (
    <section className='common-inputs-content'>
      {!isMulti && (
        <div className="common-inputs-header">
          <h3>{t('new_context_of_the_critical_issue_and_root_cause')}</h3>
          <span>{t('root_cause_info')}</span>
        </div>
      )}

      <div className='common-inputs-row'>
        {!isMulti ? (
          <div className="horizontal-row">
            <div className="label-select-container">
              <p className="label">{t('critical_issue')}</p>
              <Autocomplete
                id="search-criticl-issues"
                options={criticalIssueListShared?.filter(critical => critical.categoryName !== null)}
                getOptionLabel={displayLabel}
                value={criticalIssue}
                onChange={handleCriticalIssueChangeShared}
                renderInput={params => <TextField placeholder={t('select_item')} {...params} fullWidth />}
                noOptionsText={t('no_results')}
                classes={formErrors?.criticalIssue ? classes : {}}
                getOptionSelected={(option, value) => option.id === value.id}
                disabled={(criticalIssue && criticalIssue.isSuggesttion) || issuggestedAgreement ? true : false}
              />
              {formErrors?.criticalIssue && <RequiredErrorMessage isNotRequired={isNotRequired} />}
            </div>
            <div className="label-select-container multiple-tags" style={{ width: '100%' }}>
              {productFormShared?.length !== 0 ? (
                <>
                  <p className="label">{t('new_linked_product')}</p>
                  <Autocomplete
                    groupBy={option => `${t('new_component')} ${option.componentOrder}`}
                    multiple
                    value={linkedProductShared}
                    id="checkboxes-tags-demo"
                    options={productFormShared}
                    onChange={handleLinkedProductShared}
                    disableCloseOnSelect
                    noOptionsText={t('no_results')}
                    getOptionSelected={(option, value) => option.id === value.id}
                    getOptionLabel={displayName}
                    limitTags={3}
                    renderOption={(option, { selected }) => (
                      <OptionWithCheckBox
                        option={option}
                        selected={selected}
                      />)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <TagOptionSelected key={index} label={option.name} getTagProps={getTagProps} index={index} />
                      ))
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={linkedProductShared.length !== 0 ? ' ' : t('products')}
                        fullWidth
                      />
                    )}
                    classes={formErrors?.linkedProductsAccordance ? classes : {}}
                  />
                  {formErrors?.linkedProductsAccordance && <RequiredErrorMessage isNotRequired={isNotRequired} />}
                </>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="horizontal-row">
          <div className="label-select-container">
            <p className="label">{t('new_root_cause_of_critical_issue')}</p>
            <Autocomplete
              id="checkboxes-tags-demo"
              onChange={handleRootCauseOfCritical}
              value={rootCauseOfCritical}
              options={rootCauseCategoriesForm}
              getOptionLabel={option => option.name}
              getOptionSelected={(option, value) => option.id === value.id}
              renderInput={params => (
                <TextField {...params} label="" placeholder={t('new_all_categories')} />
              )}
              classes={formErrors?.rootCauseOfCritical ? classes : {}}
              noOptionsText={t('no_results')}
            />
            {formErrors?.rootCauseOfCritical && <RequiredErrorMessage isNotRequired={isNotRequired} />}
          </div>
          <div className="label-select-container category">
            <Autocomplete
              id="checkboxes-tags-demo"
              options={rootCauseForm}
              onChange={handleRootCause}
              value={rootCause}
              getOptionLabel={option => option.name}
              getOptionSelected={(option, value) => option.id === value.id}
              renderInput={params => (
                <TextField {...params} label="" placeholder={t('new_sub_categories')} />
              )}
              classes={formErrors?.rootCause ? classes : {}}
              noOptionsText={t('no_results')}
            />
            {formErrors?.rootCause && <RequiredErrorMessage isNotRequired={isNotRequired} />}
          </div>
        </div>

        {!isMulti ? (
          <div className="horizontal-row">
            <div className="label-select-container text-area">
              <div className="label-selec-number-caracteres">
                <p className="label">{`${t('description_of_the_critical_issue')}`}</p>
                <p>{`${criticalIssueDescriptionShared?.length === undefined ? 0 : criticalIssueDescriptionShared?.length
                  }/${MAX_LENGHT_DESCRIPTION}`}</p>
              </div>

              <div className={`content-div-with-ai ${formErrors?.description ? (isNotRequired ? 'warning-border' : 'error-border') : ''}`}>
                <textarea
                  ref={criticalIssueDescriptionRef}
                  className="textarea-with-ai"
                  maxLength={MAX_LENGHT_DESCRIPTION}
                  value={criticalIssueDescriptionShared}
                  onChange={e => handleCriticalIssueDescriptionChange(e.target.value)}
                  onBlur={e => handleCriticalIssueDescriptionChange(e.target.value)}
                  placeholder={t('new_enter_more_details_about_the_critical_issue_that_the_new_agreement_will_resolve_two')}
                ></textarea>

                {!isAppliedIASugestion &&
                  <ImproveWithAIButton
                    disabled={!showButtonIA}
                    isLoading={isLoadingIA}
                    handleClick={() => handleImproveWithAI(criticalIssueDescriptionShared, 'description_of_the_critical_issue')}
                    customText={isLoadingIA ? t('analizing').toUpperCase() : undefined}
                    IAResponse={IAResponse}
                    IAErrorRequest={IAErrorRequest}
                    suggestedText={IAResponse?.answer}
                    onUse={handleUseIAResponse}
                    onDiscard={handleDiscardIAResponse}
                    onRetry={() => {
                      setIAErrorRequest(null);
                      handleImproveWithAI(criticalIssueDescriptionShared, 'description_of_the_critical_issue');
                    }}
                    onCancel={() => setIAErrorRequest(null)}
                  />}
              </div>
              {formErrors?.criticalIssueDescription && <RequiredErrorMessage isNotRequired={isNotRequired} />}
            </div>
          </div>
        ) : (
            <div className="horizontal-row">
              <div className="label-select-container text-area">
                <div className="label-selec-number-caracteres">
                  <p className="label">{`${t('accordance_description')}`}</p>
                  <p>{`${description?.length === undefined ? 0 : description?.length}/${MAX_LENGHT_DESCRIPTION}`}</p>
                </div>
                <textarea
                  className={formErrors?.description ? (isNotRequired ? 'warning-border' : 'error-border') : ''}
                  maxLength={MAX_LENGHT_DESCRIPTION}
                  value={description}
                  onChange={e => {
                    validateTextareaDescription(e.target.value);
                  }}
                  onBlur={e => handleDescription(e.target.value)}
                  placeholder={t('new_describe_in_detail_what_the_agreement_is_about_two')}
                ></textarea>

                {formErrors?.description && <RequiredErrorMessage isNotRequired={isNotRequired} />}
              </div>
            </div>
          )}

        {!isMulti ? null : (
          <>
            <div className="horizontal-row">
              <div className="label-select-container">
                <p className="label">{t('new_bid_responsible')}</p>
                {editAccordanceData && accordanceState === AccordanceState.DRAFT ? (
                  <span className="label">
                    {editAccordanceData.personInCharge.name} {editAccordanceData.personInCharge.lastName}
                  </span>
                ) : (
                    <SearchUsers
                      selected={responsible}
                      itemSelected={handleIemSelected}
                      error={formErrors?.responsible}
                      isNotRequired={isNotRequired}
                    />
                  )}
                <p className="label-observation">{t('new_assign_to_the_manager')}</p>
                {formErrors?.responsible && <RequiredErrorMessage isNotRequired={isNotRequired} />}
              </div>
            </div>
            <div className="horizontal-row">
              <div className="label-select-container category-date">
                <CustomDatePicker
                  date={expirationDate}
                  label={t('due_date')}
                  setDate={handleExpirationDate}
                  error={formErrors?.expirationDate}
                  isNotRequired={isNotRequired}
                  minDate={revisionDate}
                  disabled={!revisionDate}
                  placeholderComplete={true}
                />
                {formErrors?.expirationDate && <RequiredErrorMessage isNotRequired={isNotRequired} />}
              </div>
            </div>
          </>
        )}

        {removeAccordance && addNewAccordance ? (
          <div className="horizontal-column">
            <div className="label-select-container ">
              <button
                className="multi-accordances-link delete multi-accordances-link-delete"
                onClick={() => removeAccordance()}
                style={{ position: 'relative', bottom: 'unset' }}
              >
                {t('delete')}
              </button>
            </div>
            <div className="label-select-container container-multi-accordances-button">
              <button
                className="multi-accordances-link "
                onClick={() => addNewAccordance()}
                style={{ position: 'relative', bottom: 'unset' }}
              >
                <div className="multi-accordances-buttton"></div>
                {t('save_and_add_to_bundle')}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default SingleInputs;
